import type { HouseholdMember } from '@domain/household'
import { Avatar } from '@ui/avatar/Avatar'

type Props = {
  members: HouseholdMember[]
  /** O uid da sessão, só para marcar qual linha é a sua. */
  currentUserId: string
}

/**
 * Quem já entrou nesta casa — e nada além disso.
 *
 * **A lista não nomeia quem falta**, e isso não é omissão: membro sem uid não
 * vira documento (decisão 46) e a allowlist mora em `functions/src/allowlist.ts`,
 * pacote que o app não importa. Não existe fonte legível pelo cliente para a
 * pessoa pendente. As duas saídas seriam copiar a lista de controle de acesso
 * para o cliente — uma terceira cópia, a pior candidata a existir — ou fazer o
 * bootstrap persistir o pendente, mudança de servidor fora do escopo desta
 * tela. A linha neutra abaixo é o que dá para dizer sem inventar dado.
 *
 * **As linhas vêm do documento de membro, inclusive a sua**, e o perfil logo
 * acima vem da sessão. A assimetria é de propósito: `displayName` e `photoURL`
 * congelam no primeiro login (o bootstrap só grava esses campos quando vincula
 * o uid — ver `linkMemberUid`), então para a **outra pessoa** o documento é a
 * única fonte que existe, e o valor congelado é o certo porque é tudo que
 * temos. Para **você**, a sessão é a fonte fresca — e é ela que o perfil usa.
 * Esta lista é o que a casa registrou, não o que o navegador sabe agora.
 *
 * Sem `frost` nas linhas: item de lista não é vidro (Docs/04-Design/Liquid Glass.md).
 */
export function MemberList({ members, currentUserId }: Props) {
  return (
    <>
      <ul className="flex flex-col gap-2">
        {members.map((member) => (
          <li
            key={member.userId ?? member.email}
            className="flex items-center gap-3 rounded-[var(--radius-md)] bg-[var(--surface-sunken)] px-3 py-2"
          >
            <Avatar displayName={member.displayName} photoURL={member.photoURL} size={36} />
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium">
                {member.displayName ?? member.email}
                {member.userId === currentUserId && (
                  <span className="ml-1 font-normal text-[var(--text-muted)]">(você)</span>
                )}
              </span>
              {/* Só quando há nome: sem ele a linha de cima já é o e-mail, e
                  repeti-lo seria o mesmo dado duas vezes. */}
              {member.displayName !== null && (
                <span className="block truncate text-xs text-[var(--text-muted)]">
                  {member.email}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>

      {/* Só um membro: a outra pessoa está na allowlist e ainda não entrou. Não
          testamos `< 2` porque zero é inalcançável — a tela só é montada no
          estado `ready`, e chegar ali significa que o bootstrap escreveu o seu
          próprio `members/{uid}`. */}
      {members.length === 1 && (
        <p className="mt-3 text-sm text-[var(--text-muted)]">
          Quando a outra pessoa entrar pela primeira vez, ela aparece aqui.
        </p>
      )}
    </>
  )
}
